import React, { useEffect, useRef, useState } from "react";
import { mixUrl, takeChannel } from "@/lib/audioPool";
import { AnimatePresence, motion } from "framer-motion";
import { RotateCcw, Sparkles, X } from "lucide-react";
import { IntroParticles } from "@/components/clientDemo/intro/IntroFx";
import { Medallion, Motes, SunBurst, TypedTitle } from "./ExchangeIntro";
import ExchangePilotForm from "./ExchangePilotForm";

const GOLD = ["#F4D88A", "#D4AF37", "#C9A227", "#FFF4CC"];
const BEATS = { medallion: 900, title: 2200, tagline: 3600, serving: 4700, path: 6000, mandate: 7000 };
const MIN_MS = 9500;
const MAX_MS = 25000;
const BURST_MS = 1500;

function useBeat() {
  const [t, setT] = useState(0);
  useEffect(() => {
    const ids = Object.values(BEATS).map((ms) => setTimeout(() => setT(ms), ms));
    return () => ids.forEach(clearTimeout);
  }, []);
  return (key) => t >= BEATS[key];
}

// Gold rays collapse inward onto the medallion — the entrance sunburst in reverse.
function Implosion() {
  return (
    <div aria-hidden data-testid="exchange-outro-implosion" className="absolute inset-0 flex items-center justify-center pointer-events-none z-0">
      <motion.div className="absolute w-[160vmax] h-[160vmax] rounded-full"
        style={{ background: "repeating-conic-gradient(from 0deg, rgba(244,216,138,0.5) 0deg 4deg, transparent 4deg 15deg)", WebkitMaskImage: "radial-gradient(circle, #000 0%, #000 25%, transparent 62%)", maskImage: "radial-gradient(circle, #000 0%, #000 25%, transparent 62%)" }}
        initial={{ scale: 1.3, opacity: 0.9, rotate: 40 }} animate={{ scale: 0.02, opacity: [0.9, 1, 0], rotate: 0 }} transition={{ duration: 1.2, ease: [0.6, 0, 0.4, 1] }} />
      <motion.div className="absolute inset-0 bg-[#FFF4CC]" initial={{ opacity: 0.85 }} animate={{ opacity: 0 }} transition={{ duration: 0.9 }} />
    </div>
  );
}

function PilotPill({ show, onClick }) {
  return (
    <div className="h-20 flex items-center justify-center">
      <AnimatePresence>
        {show && (
          <motion.button type="button" onClick={onClick} data-testid="exchange-outro-book-pilot"
            initial={{ opacity: 0, scale: 0.6, y: 10 }} animate={{ opacity: 1, scale: 1, y: 0 }} transition={{ type: "spring", stiffness: 260, damping: 18 }}
            className="relative inline-flex items-center gap-2 rounded-full bg-gradient-to-r from-[#C9A227] via-[#F4D88A] to-[#C9A227] px-9 py-4 text-lg font-extrabold tracking-wide text-[#0A0612] hover:brightness-110 active:scale-95 transition-[filter,transform]">
            <motion.span aria-hidden className="absolute inset-0 rounded-full" style={{ boxShadow: "0 0 0 0 rgba(244,216,138,0.7)" }}
              animate={{ boxShadow: ["0 0 0 0 rgba(244,216,138,0.65)", "0 0 0 22px rgba(244,216,138,0)"] }} transition={{ duration: 1.6, repeat: Infinity, ease: "easeOut" }} />
            <motion.span className="relative inline-flex items-center gap-2" animate={{ scale: [1, 1.06, 1] }} transition={{ duration: 1.6, repeat: Infinity, ease: "easeInOut" }}>
              <Sparkles className="w-5 h-5" /> Book a Pilot
            </motion.span>
          </motion.button>
        )}
      </AnimatePresence>
    </div>
  );
}

function PilotModal({ onClose }) {
  return (
    <motion.div data-testid="exchange-outro-form-modal" className="absolute inset-0 z-40 flex items-center justify-center bg-black/70 backdrop-blur-sm px-4"
      initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
      <motion.div initial={{ y: 24, opacity: 0 }} animate={{ y: 0, opacity: 1 }} className="relative w-full max-w-xl rounded-3xl border border-[#D4AF37]/40 bg-[#0A0612] p-6 md:p-8">
        <button type="button" onClick={onClose} data-testid="exchange-outro-form-close" aria-label="Close form" className="absolute top-4 right-4 text-slate-400 hover:text-white transition-colors"><X className="w-5 h-5" /></button>
        <div className="font-mono text-[10px] tracking-[0.28em] uppercase text-[#C9A227]">// 90-day pilot</div>
        <h3 className="mt-1 text-2xl font-extrabold text-white">Let&apos;s prove it in your community.</h3>
        <ExchangePilotForm />
      </motion.div>
    </motion.div>
  );
}

function useOutroAudio(musicUrl, narrationUrl, onDone) {
  const doneRef = useRef(onDone);
  doneRef.current = onDone;
  useEffect(() => {
    const started = Date.now();
    let fired = false;
    const finish = () => { if (!fired) { fired = true; setTimeout(() => doneRef.current(), Math.max(0, MIN_MS - (Date.now() - started))); } };
    const mh = musicUrl ? takeChannel("music", mixUrl(musicUrl, 0.6)) : null;
    const nh = narrationUrl ? takeChannel("voice", narrationUrl) : null;
    const n = nh?.el || null;
    mh?.play().catch(() => {});
    let nt;
    if (n) {
      n.onended = () => setTimeout(finish, 500);
      n.onerror = finish;
      nt = setTimeout(() => nh.play().catch(finish), 1300);
    } else finish();
    const cap = setTimeout(finish, MAX_MS);
    return () => {
      clearTimeout(nt); clearTimeout(cap);
      [mh, nh].forEach((h) => h?.release());
    };
  }, [musicUrl, narrationUrl]);
}

// Power exit — the finale mirrors the entrance, then a pulsing Book a Pilot pill lands under the medallion.
export default function ExchangeOutro({ logoUrl, musicUrl, narrationUrl, onClose, onReplay }) {
  const at = useBeat();
  const medRef = useRef(null);
  const [burst, setBurst] = useState(null);
  const [ready, setReady] = useState(false);
  const [form, setForm] = useState(false);
  useOutroAudio(musicUrl, narrationUrl, () => {
    const r = medRef.current?.getBoundingClientRect();
    setBurst(r ? { x: r.left + r.width / 2, y: r.top + r.height / 2 } : { x: window.innerWidth / 2, y: window.innerHeight / 2 });
    setTimeout(() => setReady(true), BURST_MS - 400);
  });

  return (
    <motion.div data-testid="exchange-outro" data-ready={ready ? "1" : "0"} className="fixed inset-0 z-[130] bg-[#0A0612] text-white overflow-hidden flex items-center justify-center"
      initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.6 }}>
      <div aria-hidden className="absolute inset-0 opacity-60" style={{ background: "radial-gradient(ellipse at 50% 40%, rgba(212,175,55,0.22), transparent 60%), radial-gradient(ellipse at 50% 110%, rgba(15,118,110,0.3), transparent 55%)" }} />
      <Implosion />
      <Motes />
      {at("tagline") && <IntroParticles count={54} colors={GOLD} />}
      {burst && <motion.div className="absolute inset-0 pointer-events-none" initial={{ opacity: 1 }} animate={{ opacity: 0 }} transition={{ delay: 0.9, duration: 1.4 }}><SunBurst origin={burst} /></motion.div>}

      <div className="relative z-10 flex flex-col items-center text-center px-6">
        <Medallion logoUrl={logoUrl} sweep innerRef={medRef} delay={BEATS.medallion} />
        <div className="mt-6"><PilotPill show={ready} onClick={() => setForm(true)} /></div>
        <div className="mt-4 min-h-[1.2em]">{at("title") && <TypedTitle testId="exchange-outro-title" />}</div>
        <div className="mt-8 h-px w-40 bg-gradient-to-r from-transparent via-[#D4AF37] to-transparent" style={{ opacity: at("tagline") ? 1 : 0, transition: "opacity .6s" }} />
        <div className="mt-6 min-h-[2.5rem]">
          {at("tagline") && (
            <motion.p data-testid="exchange-outro-tagline" className="text-lg sm:text-2xl lg:text-3xl font-light italic text-[#FAFAF7]"
              initial={{ opacity: 0, filter: "blur(6px)" }} animate={{ opacity: 1, filter: "blur(0px)" }} transition={{ duration: 0.8 }}>
              From crisis to self-sufficiency.
            </motion.p>
          )}
        </div>
        <div className="mt-3 min-h-[2.25rem]">
          {at("serving") && (
            <motion.p data-testid="exchange-outro-serving" className="text-base sm:text-xl lg:text-2xl font-light italic"
              initial={{ opacity: 0, y: 10, filter: "blur(6px)" }} animate={{ opacity: 1, y: 0, filter: "blur(0px)" }} transition={{ duration: 0.8 }}>
              <motion.span className="bg-gradient-to-r from-[#FAFAF7] via-[#F4D88A] to-[#FAFAF7] bg-clip-text text-transparent bg-[length:200%_100%]"
                animate={{ backgroundPosition: ["0% 0%", "200% 0%"] }} transition={{ duration: 4, repeat: Infinity, ease: "linear" }}>
                Serving the Last, the Least and the Lost
              </motion.span>
            </motion.p>
          )}
        </div>
        <p className="mt-3 h-5 font-mono text-[11px] sm:text-xs tracking-[0.4em] uppercase text-[#F4D88A]" style={{ opacity: at("path") ? 1 : 0, transition: "opacity .7s" }}>
          One Path · One Proven Result
        </p>
        <p data-testid="exchange-outro-mandate" className="mt-2 h-5 font-mono text-[11px] sm:text-xs tracking-[0.4em] uppercase text-[#D4AF37]" style={{ opacity: at("mandate") ? 1 : 0, transition: "opacity .7s" }}>
          His Mission · His Mandate
        </p>
      </div>

      <div className="absolute top-5 right-5 z-20 flex gap-2">
        <button type="button" onClick={onReplay} data-testid="exchange-outro-replay"
          className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg bg-white/5 border border-[#D4AF37]/30 text-[#F4D88A] hover:bg-[#D4AF37]/15 font-mono text-[10px] tracking-[0.16em] uppercase transition-colors">
          <RotateCcw className="w-3.5 h-3.5" /> Replay
        </button>
        <button type="button" onClick={onClose} data-testid="exchange-outro-close"
          className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg bg-white/5 border border-[#D4AF37]/30 text-[#F4D88A] hover:bg-[#D4AF37]/15 font-mono text-[10px] tracking-[0.16em] uppercase transition-colors">
          Close <X className="w-3.5 h-3.5" />
        </button>
      </div>
      <AnimatePresence>{form && <PilotModal onClose={() => setForm(false)} />}</AnimatePresence>
    </motion.div>
  );
}
